// ── Token accounting (O-2047, extended) ─────────────────────────────────────
// One source of truth for how the dashboard decomposes token volume, shared by
// query construction (apiClient) and every consumer surface (the Token-volume
// chart, the trace-list summary bar, the trace-detail chip, scanVolume's
// shapeScanVolume). The alias contract below is the boundary those callers
// agree on.
//
// Five raw buckets land on each token_observation row (analytics_query.proto
// METRIC_TYPE_TOKEN_SUM_*). Their accounting is NOT uniform across providers,
// and two of them are NOT additive to a token total — get this wrong and the
// numbers either double-count or invent traffic that never happened:
//
//   input_tokens          Non-cached billable input. Anthropic reports this
//                         DISJOINT from cache (cache reads/writes are NOT in
//                         here); OpenAI / Gemini report input INCLUSIVE of the
//                         cached portion.
//   output_tokens         Completion. Anthropic folds thinking into this, so
//                         Claude reasoning is already counted here.
//   cache_read_tokens     Prompt-cache reads. Cheap, and on agentic Anthropic
//                         workloads routinely 100-400× the uncached input — a
//                         HIGH read:input ratio means caching is working; a
//                         collapse toward ~0 is the alarm worth surfacing.
//                         Measured for Anthropic and some inclusive-input
//                         providers (including the Codex transcript path). A
//                         zero where the provider does not populate it means
//                         "not measured", never "cache broke."
//   cache_creation_tokens Cache writes (Anthropic-specific, billed at a
//                         premium). Same Anthropic-only caveat as reads.
//   reasoning_tokens      OpenAI o-series / DeepSeek only; Claude is NULL by
//                         design (thinking lives in output_tokens). A SUBSET of
//                         output, never additive — it's a breakdown of the
//                         output band, not a fifth bucket.
//
// Canonical derived figures — deliberately distinct, one definition each, so
// the summary bar / chip / legend never show the same concept two different ways:
//   WORK        = input + output. Provider-consistent, cache-independent. The
//                 clean headline ("total"). reasoning is already inside output,
//                 so WORK counts it exactly once.
//   THROUGHPUT  = input + cache_read + cache_creation + output. Everything
//                 processed on the wire ("processed"); cache_read dominates.
//                 The cost / volume view, NOT a "how much work" number. Each
//                 band's share of THROUGHPUT is the legend's composition %.
//   CONTEXT     = input + cache_read + cache_creation. The prompt-side tokens
//                 the model conditioned on (throughput minus output) — the
//                 chip's headline for a turn.
//   CACHE REUSE = cache_read / CONTEXT. THE cache-health metric: how much of
//                 the prompt was served from cache. High = reuse paying off,
//                 low/0 = the broken-cache alarm. This is the single definition
//                 of "% cached" — use cacheReuseRate everywhere that phrase
//                 appears so chip and legend agree (it is NOT a band's share of
//                 throughput, which is composition, a different number).
//
// Coverage (calls_total / calls_with_tokens) is the honesty signal: NULL token
// columns are skipped by sum() rather than counted as 0, so a total can be
// real-but-partial. calls_with_tokens / calls_total is the fraction of activity
// actually measured — read every breakdown against it before trusting a low
// number (a "broken cache" reading on poor coverage is unmeasured, not broken).
//
// The cache columns are summed provider-predicated (sumIf provider='anthropic')
// so they contribute only where the provider keeps cache OUTSIDE input_tokens;
// adding them for OpenAI / Gemini (inclusive input) would double-count. Keyed
// off provider (reporting semantics), not model name — gateways relaying Claude
// models normalize to OpenAI-shaped inclusive usage, so cache must NOT be added
// for them either (providerCacheApplies stays false; the inclusive input
// already carries the cached tokens).

// Providers whose cache columns are DISJOINT from input_tokens — the only ones
// where cache_read / cache_creation may be added to a total or read as a
// cache-health signal. Everyone else reports cache-inclusive input (or doesn't
// break cache out at all), so their cache columns are informational / zero,
// never additive.
const CACHE_DISJOINT_PROVIDERS = new Set(["anthropic"]);

// Does this provider's accounting keep prompt-cache tokens OUTSIDE input_tokens
// (so the cache columns are real, additive, and a meaningful health signal)?
// Gate cache DISPLAY per-trace on this: a non-applicable provider's "0 cache"
// means unmeasured, not a broken cache, and must not render as the latter.
export function providerCacheApplies(provider) {
  return CACHE_DISJOINT_PROVIDERS.has(String(provider || "").toLowerCase());
}

// Output aliases that add up to a row's THROUGHPUT (all processed tokens).
export const TOKEN_VOLUME_ALIASES = ["input_tokens", "output_tokens", "cache_read_tokens", "cache_creation_tokens"];

// Output aliases that add up to WORK (net-new tokens; the clean headline).
export const WORK_TOKEN_ALIASES = ["input_tokens", "output_tokens"];

// Metric refs for the provider-predicated cache sums; spread into a spec's
// `metrics` alongside the input/output sums on the same TOKEN step. The
// predicate emits sumIf(col, provider = 'anthropic') — see the note above.
export function cacheTokenMetrics(stepAlias) {
  const predicate = {
    pred: {
      field: { stepAlias, field: "DIMENSION_FIELD_TOKEN_PROVIDER" },
      op: "OPERATOR_EQ",
      value: "anthropic",
    },
  };
  return [
    { outputAlias: "cache_read_tokens",     standard: { stepAlias, metric: "METRIC_TYPE_TOKEN_SUM_CACHED_INPUT_TOKENS", predicate } },
    { outputAlias: "cache_creation_tokens", standard: { stepAlias, metric: "METRIC_TYPE_TOKEN_SUM_CACHE_CREATION_INPUT_TOKENS", predicate } },
  ];
}

// Cached-input tokens for inclusive-input providers. This is a subset of
// input_tokens used only for pricing; it must never be added to volume totals.
export function inclusiveCacheMetric(stepAlias) {
  const predicate = {
    pred: {
      field: { stepAlias, field: "DIMENSION_FIELD_TOKEN_PROVIDER" },
      op: "OPERATOR_NE",
      value: "anthropic",
    },
  };
  return [
    { outputAlias: "inclusive_cache_read_tokens", standard: { stepAlias, metric: "METRIC_TYPE_TOKEN_SUM_CACHED_INPUT_TOKENS", predicate } },
  ];
}

// Reasoning / thinking tokens (OpenAI o-series, DeepSeek). No predicate: the
// column is NULL wherever a provider doesn't expose a dedicated count
// (Anthropic folds thinking into output), so the sum is naturally
// provider-correct. A SUBSET of output — surface it as a breakdown, never add
// it to a total.
export function reasoningTokenMetric(stepAlias) {
  return [
    { outputAlias: "reasoning_tokens", standard: { stepAlias, metric: "METRIC_TYPE_TOKEN_SUM_REASONING_TOKENS" } },
  ];
}

// Coverage counters: how many API calls a total covers vs how many had
// parseable token counts. calls_with_tokens / calls_total is the fraction of
// activity actually measured — the honesty signal for every token number.
export function coverageMetrics(stepAlias) {
  return [
    { outputAlias: "calls_total",       standard: { stepAlias, metric: "METRIC_TYPE_TOKEN_CALLS_TOTAL" } },
    { outputAlias: "calls_with_tokens", standard: { stepAlias, metric: "METRIC_TYPE_TOKEN_CALLS_WITH_TOKENS" } },
  ];
}

// Row total across the four THROUGHPUT aliases. sumIf over zero matching rows
// returns NULL (not 0), and rows from pre-O-2047 callers lack the cache aliases
// entirely — the `|| 0` coercion absorbs both.
export function tokenVolumeOf(row) {
  let total = 0;
  for (const alias of TOKEN_VOLUME_ALIASES) total += Number(row[alias]) || 0;
  return total;
}

// Row WORK total (input + output) — the cache-independent headline figure.
export function workVolumeOf(row) {
  let total = 0;
  for (const alias of WORK_TOKEN_ALIASES) total += Number(row[alias]) || 0;
  return total;
}

// CONTEXT — prompt-side tokens the model conditioned on this turn (fresh input
// + cache reads + cache writes; output is the other axis). Takes camelCase
// breakdown fields ({ input, cacheRead, cacheCreation }) so the chip, summary bar,
// and legend can all pass the same shape.
export function contextOf({ input, cacheRead, cacheCreation } = {}) {
  return (Number(input) || 0) + (Number(cacheRead) || 0) + (Number(cacheCreation) || 0);
}

// CACHE REUSE — share of the context served from cache (cache_read ÷ context).
// The one definition of "% cached" across every surface (chip headline, summary-bar
// hover card, legend footer), so the same data never shows two different cache %s.
// Returns null when there's no context to divide by (avoids a misleading 0%).
export function cacheReuseRate(parts) {
  const ctx = contextOf(parts);
  return ctx > 0 ? (Number(parts?.cacheRead) || 0) / ctx : null;
}

// Aggregate a set of token_observation rows (each carrying the alias columns
// above) into one scope-level breakdown. NULLs coerce to 0; `work` and
// `throughput` are derived here so consumers never re-implement the accounting.
// Coverage is carried through raw (callsTotal / callsWithTokens) for the
// consumer to format against its own threshold.
export function sumTokenBreakdown(rows) {
  const acc = {
    input: 0, output: 0,
    cacheRead: 0, cacheCreation: 0,
    inclusiveCacheRead: 0,
    reasoning: 0,
    callsTotal: 0, callsWithTokens: 0,
  };
  for (const r of rows || []) {
    acc.input          += Number(r.input_tokens) || 0;
    acc.output         += Number(r.output_tokens) || 0;
    acc.cacheRead      += Number(r.cache_read_tokens) || 0;
    acc.cacheCreation  += Number(r.cache_creation_tokens) || 0;
    acc.inclusiveCacheRead += Number(r.inclusive_cache_read_tokens) || 0;
    acc.reasoning      += Number(r.reasoning_tokens) || 0;
    acc.callsTotal     += Number(r.calls_total) || 0;
    acc.callsWithTokens += Number(r.calls_with_tokens) || 0;
  }
  acc.work = acc.input + acc.output;
  acc.throughput = acc.input + acc.output + acc.cacheRead + acc.cacheCreation;
  return acc;
}
